import { FC, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiDetailFields } from "@miplatform/components";
import { INPUT_TYPES, PERMISSIONS } from "@miplatform/constants";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Grid, TextField, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IOrderTemplateForm } from "../ConfigOrderTemplates.types";

export const OrderAccountBillingTabView: FC<
  OrderAccountBillingTabViewProps
> = ({ form, extraTabProps: { isReadOnly } = {} }) => {
  const theme = useTheme();
  const { control, setValue, watch } = form;
  const { codPaymentAllowed, invoicePaymentAllowed, costCodes = [] } = watch();
  const { hasPermission } = usePermissions();

  const canManageCostCodes = hasPermission(
    PERMISSIONS.ORDER_TEMPLATE.MANAGE_COST_CODES
  );

  const isDisabled =
    (!codPaymentAllowed && !invoicePaymentAllowed) || isReadOnly;

  const accountBillingFields = [
    {
      label: "Billable Orders",
      name: "codPaymentAllowed",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      width: "100%",
      disabled: isReadOnly,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("codPaymentAllowed", e.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("invoicePaymentAllowed", e.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("isPrebill", false, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("fulfillmentRequiredForInvoicing", false, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      label: "Match Costs Before Invoicing",
      name: "isPrebill",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      width: "100%",
      disabled: isDisabled,
    },
    {
      label: "Work Must Be Completed Before Invoicing",
      name: "fulfillmentRequiredForInvoicing",
      formControl: control,
      fieldType: INPUT_TYPES.SWITCH,
      width: "100%",
      disabled: isDisabled,
    },
  ];

  const handleCostCodeChange = useCallback(
    (accountId: string, value: string) => {
      const updatedCostCodes = costCodes.map((cc) =>
        cc.accountId === accountId ? { ...cc, value } : cc
      );
      setValue("costCodes", updatedCostCodes, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [costCodes, setValue]
  );

  return (
    <>
      <ConfigAccordionHeader title="Accounting & Billing">
        <MiDetailFields detailFields={accountBillingFields} />
      </ConfigAccordionHeader>

      {canManageCostCodes && (
        <Box sx={{ marginTop: theme.spacing(2) }}>
          <ConfigAccordionHeader title="Builder Cost Codes">
            <Grid container>
              {costCodes.map((costCode) => (
                <Grid width="50%" key={costCode.accountId} padding={2}>
                  <Typography
                    sx={{
                      marginLeft: theme.spacing(2),
                      marginBottom: theme.spacing(2),
                      fontWeight: theme.fontWeight.semiBold,
                    }}
                  >
                    {costCode.accountName}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Cost Code"
                    value={costCode.value || ""}
                    onChange={(e) =>
                      handleCostCodeChange(costCode.accountId, e.target.value)
                    }
                    disabled={isReadOnly}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </ConfigAccordionHeader>
        </Box>
      )}
    </>
  );
};

interface OrderAccountBillingTabViewProps {
  form: UseFormReturn<IOrderTemplateForm>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
